import { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  GARMENT_CATEGORIES,
  OCCASIONS,
  SEASONS,
  type GarmentCategory,
  type Occasion,
  type Season,
} from '../../shared/wardrobe';
import type {
  EditableGarmentSuggestion,
  GarmentSuggestion,
} from '../../shared/smartDetector';
import { startGarmentImageJob } from '../lib/imageProcessing';
import { createWardrobeItem } from '../lib/items';
import { analyzeGarmentPhoto, cropGarmentPhoto } from '../lib/smartDetector';
import {
  fetchStudioPreviewImage,
  startStudioPreview,
  waitForStudioPreview,
  type StudioPreview,
} from '../lib/studioPreview';
import { Icon } from './Icon';

type CleanPreviewState =
  | { status: 'processing' }
  | { status: 'completed'; url: string }
  | { status: 'failed' };

function GarmentPreview({
  previewUrl,
  crop,
}: {
  previewUrl: string;
  crop: EditableGarmentSuggestion['crop'];
}) {
  const canvas = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!previewUrl || !canvas.current) return;
    const image = new Image();
    image.onload = () => {
      const target = canvas.current;
      if (!target) return;
      const sourceWidth = Math.max(1, crop.width * image.naturalWidth);
      const sourceHeight = Math.max(1, crop.height * image.naturalHeight);
      target.width = 360;
      target.height = Math.max(150, Math.min(360, Math.round(360 * (sourceHeight / sourceWidth))));
      target
        .getContext('2d')
        ?.drawImage(
          image,
          crop.x * image.naturalWidth,
          crop.y * image.naturalHeight,
          sourceWidth,
          sourceHeight,
          0,
          0,
          target.width,
          target.height,
        );
    };
    image.src = previewUrl;
    return () => {
      image.onload = null;
    };
  }, [crop, previewUrl]);

  return (
    <div className="crop-editor">
      <canvas aria-label="Detected garment preview" ref={canvas} />
    </div>
  );
}

export function SmartDetectorPage() {
  const navigate = useNavigate();
  const fileInput = useRef<HTMLInputElement>(null);
  const requestKeys = useRef(new Map<string, string>());
  const [photo, setPhoto] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [suggestions, setSuggestions] = useState<EditableGarmentSuggestion[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const [cleanPreviews, setCleanPreviews] = useState<Record<string, CleanPreviewState>>({});

  function setCleanPreview(id: string, state: CleanPreviewState) {
    setCleanPreviews((current) => ({ ...current, [id]: state }));
  }

  async function prepareCleanPreview(
    sourcePhoto: File,
    suggestion: GarmentSuggestion,
    index: number,
    analysisKey: string,
  ) {
    setCleanPreview(suggestion.id, { status: 'processing' });
    try {
      const cropped = await cropGarmentPhoto(sourcePhoto, suggestion.crop, index);
      const started = await startStudioPreview(cropped, `${analysisKey}-${suggestion.id}`, {
        name: suggestion.name,
        category: suggestion.category,
        primaryColor: suggestion.primaryColor,
      });
      const finished: StudioPreview =
        started.status === 'processing' ? await waitForStudioPreview(started.id) : started;
      if (finished.status === 'completed' && finished.previewUrl) {
        setCleanPreview(suggestion.id, { status: 'completed', url: finished.previewUrl });
      } else {
        setCleanPreview(suggestion.id, { status: 'failed' });
      }
    } catch {
      setCleanPreview(suggestion.id, { status: 'failed' });
    }
  }

  useEffect(() => {
    if (!photo) {
      setPreviewUrl('');
      return;
    }

    const url = URL.createObjectURL(photo);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  function choosePhoto(nextPhoto: File | null) {
    setPhoto(nextPhoto);
    setSuggestions([]);
    setCleanPreviews({});
    setSavedCount(0);
    setErrorMessage('');
    setAnalysisStatus('');
    requestKeys.current.clear();
  }

  async function detectGarments() {
    if (!photo) {
      setErrorMessage('Choose a photo with one or more garments first.');
      fileInput.current?.focus();
      return;
    }

    setAnalyzing(true);
    setAnalysisStatus('Preparing the photo for garment detection…');
    setErrorMessage('');
    try {
      const nextAnalysisKey = crypto.randomUUID();
      const detected = await analyzeGarmentPhoto(photo, nextAnalysisKey, setAnalysisStatus);
      setSuggestions(detected.map((suggestion) => ({ ...suggestion, selected: true })));
      setCleanPreviews({});
      detected.forEach((suggestion, index) => {
        void prepareCleanPreview(photo, suggestion, index, nextAnalysisKey);
      });
    } catch (caught) {
      setErrorMessage(
        caught instanceof Error ? caught.message : 'Smart Detector could not read this photo.',
      );
    } finally {
      setAnalyzing(false);
      setAnalysisStatus('');
    }
  }

  function updateSuggestion(
    id: string,
    update: (suggestion: EditableGarmentSuggestion) => EditableGarmentSuggestion,
  ) {
    setSuggestions((current) =>
      current.map((suggestion) => (suggestion.id === id ? update(suggestion) : suggestion)),
    );
  }

  function toggleTag<T extends Season | Occasion>(
    id: string,
    field: 'seasons' | 'occasions',
    value: T,
  ) {
    updateSuggestion(id, (suggestion) => {
      const selected = suggestion[field] as T[];
      const next = selected.includes(value)
        ? selected.filter((entry) => entry !== value)
        : [...selected, value];
      return { ...suggestion, [field]: next };
    });
  }

  async function saveSelected() {
    if (!photo) return;
    const selected = suggestions.filter((suggestion) => suggestion.selected);
    if (selected.length === 0) {
      setErrorMessage('Select at least one garment to save.');
      return;
    }
    if (selected.some(({ name, primaryColor }) => !name.trim() || !primaryColor.trim())) {
      setErrorMessage('Review the name and main colour for every selected garment.');
      return;
    }

    setSaving(true);
    setSavedCount(0);
    setErrorMessage('');
    try {
      let cleanupUnavailable = false;
      for (const [index, suggestion] of selected.entries()) {
        const cleanPreview = cleanPreviews[suggestion.id];
        const uploadPhoto =
          cleanPreview?.status === 'completed'
            ? await fetchStudioPreviewImage(cleanPreview.url).catch(() => null)
            : null;
        const croppedPhoto = uploadPhoto ?? (await cropGarmentPhoto(photo, suggestion.crop, index));
        let requestKey = requestKeys.current.get(suggestion.id);
        if (!requestKey) {
          requestKey = crypto.randomUUID();
          requestKeys.current.set(suggestion.id, requestKey);
        }
        const item = await createWardrobeItem(
          {
            name: suggestion.name.trim(),
            category: suggestion.category,
            primaryColor: suggestion.primaryColor.trim(),
            seasons: suggestion.seasons,
            occasions: suggestion.occasions,
            notes: `Reviewed after Smart Detector · ${Math.round(suggestion.confidence * 100)}% initial confidence`,
          },
          croppedPhoto,
          requestKey,
        );
        if (!uploadPhoto) {
          // The reviewed clean image was unavailable, so this piece saved with
          // its plain crop; the after-save cleanup remains the fallback.
          try {
            await startGarmentImageJob(item.id, `${requestKey}-studio`, 'studio-photo', {
              autoApply: true,
            });
          } catch {
            cleanupUnavailable = true;
          }
        }
        setSavedCount(index + 1);
      }
      if (cleanupUnavailable) {
        setErrorMessage(
          'Your pieces are saved, but the clean garment-only image could not start for some of them. Open the piece in your closet and choose Create studio photo to retry.',
        );
        return;
      }
      await navigate('/closet');
    } catch (caught) {
      setErrorMessage(
        caught instanceof Error
          ? `${caught.message} ${savedCount > 0 ? `${savedCount} item(s) were already saved.` : ''}`
          : 'The selected garments could not be saved.',
      );
    } finally {
      setSaving(false);
    }
  }

  const selectedCount = suggestions.filter((suggestion) => suggestion.selected).length;

  return (
    <div className="page smart-detector-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">AI-assisted registration</p>
          <h1>Smart Detector</h1>
          <p>
            Use one everyday photo. Review every suggestion before anything reaches your closet.
          </p>
        </div>
        <NavLink className="button button--secondary" to="/add">
          Single item
        </NavLink>
      </header>

      <section className="smart-source-card">
        <div className={`smart-photo${previewUrl ? ' has-photo' : ''}`}>
          {previewUrl ? (
            <img alt="Photo selected for Smart Detector" src={previewUrl} />
          ) : (
            <div>
              <span className="upload-illustration">
                <Icon name="sparkles" size={44} />
              </span>
              <strong>Mirror shot, selfie, flat lay, or a few clothes together</strong>
              <small>The photo stays private and is never saved without your confirmation.</small>
            </div>
          )}
        </div>
        <div className="smart-source-copy">
          <p className="eyebrow">Step 1 · Choose a photo</p>
          <h2>Let the cloud find each piece</h2>
          <p>
            Clear lighting and visible garment edges work best. You will be able to correct every
            name, colour, category, season, and occasion.
          </p>
          <input
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            aria-label="Smart Detector photo"
            className="sr-only"
            ref={fileInput}
            type="file"
            onChange={(event) => choosePhoto(event.target.files?.[0] ?? null)}
          />
          <div className="smart-source-actions">
            <button
              className="button button--secondary"
              disabled={analyzing || saving}
              type="button"
              onClick={() => fileInput.current?.click()}
            >
              {photo ? 'Replace photo' : 'Choose photo'}
            </button>
            <button
              className="button button--primary"
              disabled={!photo || analyzing || saving}
              type="button"
              onClick={() => void detectGarments()}
            >
              <Icon name="sparkles" size={18} />
              {analyzing
                ? 'Finding garments…'
                : suggestions.length
                  ? 'Detect again'
                  : 'Find pieces'}
            </button>
          </div>
          {analyzing ? (
            <div aria-live="polite" className="analysis-progress" role="status">
              <span />
              {analysisStatus}
            </div>
          ) : null}
        </div>
      </section>

      {errorMessage ? (
        <p className="form-error smart-error" role="alert">
          {errorMessage}
        </p>
      ) : null}

      {suggestions.length > 0 ? (
        <section className="smart-review-section">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Step 2 · Mandatory review</p>
              <h2>Choose and correct each piece</h2>
            </div>
            <span className="soft-chip">
              {selectedCount} of {suggestions.length} selected
            </span>
          </div>

          <div className="suggestion-grid">
            {suggestions.map((suggestion, index) => (
              <article
                className={`suggestion-card${suggestion.selected ? ' is-selected' : ''}`}
                key={suggestion.id}
              >
                <div className="suggestion-card__top">
                  <label className="suggestion-select">
                    <input
                      checked={suggestion.selected}
                      type="checkbox"
                      onChange={(event) =>
                        updateSuggestion(suggestion.id, (current) => ({
                          ...current,
                          selected: event.target.checked,
                        }))
                      }
                    />
                    Save piece {index + 1}
                  </label>
                  <span className={suggestion.confidence < 0.7 ? 'is-low-confidence' : ''}>
                    {suggestion.confidence < 0.7 ? 'Needs review · ' : ''}
                    {Math.round(suggestion.confidence * 100)}% confidence
                  </span>
                </div>

                {cleanPreviews[suggestion.id]?.status === 'completed' ? (
                  <div className="crop-editor">
                    <img
                      alt={`Clean image of ${suggestion.name || `piece ${index + 1}`}`}
                      className="clean-preview-image"
                      src={(cleanPreviews[suggestion.id] as { url: string }).url}
                    />
                  </div>
                ) : cleanPreviews[suggestion.id]?.status === 'failed' ? (
                  <>
                    <GarmentPreview crop={suggestion.crop} previewUrl={previewUrl} />
                    <p aria-live="polite" className="clean-preview-status" role="status">
                      The clean image could not be prepared; this piece will be cleaned after
                      saving.
                    </p>
                  </>
                ) : (
                  <div aria-live="polite" className="clean-preview-loading" role="status">
                    <span className="clean-preview-loading__pulse" />
                    Creating the clean garment-only image…
                  </div>
                )}

                <div className="suggestion-fields">
                  <label className="field field--wide">
                    <span>Name</span>
                    <input
                      disabled={!suggestion.selected}
                      maxLength={100}
                      value={suggestion.name}
                      onChange={(event) =>
                        updateSuggestion(suggestion.id, (current) => ({
                          ...current,
                          name: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label className="field">
                    <span>Category</span>
                    <select
                      disabled={!suggestion.selected}
                      value={suggestion.category}
                      onChange={(event) =>
                        updateSuggestion(suggestion.id, (current) => ({
                          ...current,
                          category: event.target.value as GarmentCategory,
                        }))
                      }
                    >
                      {GARMENT_CATEGORIES.map((category) => (
                        <option key={category}>{category}</option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    <span>Main colour</span>
                    <input
                      disabled={!suggestion.selected}
                      maxLength={40}
                      value={suggestion.primaryColor}
                      onChange={(event) =>
                        updateSuggestion(suggestion.id, (current) => ({
                          ...current,
                          primaryColor: event.target.value,
                        }))
                      }
                    />
                  </label>
                </div>

                <div className="compact-tag-group">
                  <span>Season</span>
                  <div className="choice-chips">
                    {SEASONS.map((season) => (
                      <button
                        aria-pressed={suggestion.seasons.includes(season)}
                        className={suggestion.seasons.includes(season) ? 'is-selected' : ''}
                        disabled={!suggestion.selected}
                        key={season}
                        type="button"
                        onClick={() => toggleTag(suggestion.id, 'seasons', season)}
                      >
                        {season}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="compact-tag-group">
                  <span>Occasion</span>
                  <div className="choice-chips">
                    {OCCASIONS.map((occasion) => (
                      <button
                        aria-pressed={suggestion.occasions.includes(occasion)}
                        className={suggestion.occasions.includes(occasion) ? 'is-selected' : ''}
                        disabled={!suggestion.selected}
                        key={occasion}
                        type="button"
                        onClick={() => toggleTag(suggestion.id, 'occasions', occasion)}
                      >
                        {occasion}
                      </button>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="smart-save-bar">
            <span>
              <strong>Nothing saves automatically.</strong>
              {saving
                ? ` Saved ${savedCount} of ${selectedCount}…`
                : ' The clean garment-only image shown on each card (about $0.04 per piece) is exactly what your closet will store. Your original photo stays private and can always be restored.'}
            </span>
            <button
              className="button button--primary"
              disabled={saving || selectedCount === 0}
              type="button"
              onClick={() => void saveSelected()}
            >
              {saving ? 'Saving reviewed pieces…' : `Save ${selectedCount} selected`}
              {!saving ? <Icon name="arrow" size={18} /> : null}
            </button>
          </div>
        </section>
      ) : null}
    </div>
  );
}
